import type { IpnsString } from '@wovin/core/applog'
import type { DecodedCar } from '@wovin/core/ipfs'
import type { Thread, WriteableThread } from '@wovin/core/thread'
import type { SnapRootBlock } from '@wovin/core/pubsub'
import * as dagJson from '@ipld/dag-json'
// import { getVM } from '../data/VMs/MappedVMbase'
// import { ProviderVM } from '../data/VMs/ProviderVM'
import type { ProviderVM } from '../data/VMs/ProviderVM'
import { asyncIterableToArray } from '@note3/utils'
import * as Comlink from 'comlink'
import {
	IPNSNeverPublishedError,
	IPNSResolutionError,
	resolveIPNSViaW3Name,
	UcanStoreProxyConnector,
} from '@wovin/connect-ucan-store-proxy'
import { resolveIPNSViaGateway } from '@wovin/core/ipns'
import { areCidsEqual, decodePubFromBlocks, parseCID } from '@wovin/core/ipfs'
import { Logger } from 'besonders-logger'
import { CID } from 'multiformats/cid'
import { sanityCheckLogs } from '../data/block-utils-nowin'
import { getBlockFromStore } from './block-store'
import { getBlockStore, putManyBlocks } from './blockStore'
import { getKuboGateway, hasKubo, kuboClient } from './kubo'
import { storageState } from './storage'
import { tryIntegrateApplogs } from './store-sync'
import { clearTransferProgress, reportTransferProgress } from './transfer-progress'
import { getSyncWorker } from './worker-utils'
import { parseStoredCID } from './cid-utils'
import type { PullProgressEvent } from './sync-worker'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.DEBUG)

/**
 * Backoff for the full-chain gateway fallback (below): when car-mirror fails or its
 * verification detects missing referenced blocks, we fall back to fetching the FULL
 * publication chain via gateway — a "quota-busting" operation on constrained storage/data
 * plans. Before this backoff existed, a persistently-failing pub (e.g. one that never
 * verifies cleanly) would re-trigger this full fetch every single sync tick. Track
 * per-target (ipns id, or the pinned CID for previews) consecutive-fallback state and back
 * off exponentially, so a stuck target degrades to occasional retries instead of hammering
 * gateways every tick. Cleared as soon as a retrieval for that target completes without
 * needing the fallback, or the fallback itself succeeds.
 */
const FALLBACK_BASE_DELAY_MS = 60_000 // 1 minute
const FALLBACK_MAX_DELAY_MS = 30 * 60_000 // cap at 30 minutes
const fallbackBackoffState = new Map<string, { failures: number; nextAttemptAt: number }>()

function isFallbackBackedOff(key: string): { failures: number; nextAttemptAt: number } | null {
	const state = fallbackBackoffState.get(key)
	return state && Date.now() < state.nextAttemptAt ? state : null
}

function recordFallbackAttempt(key: string) {
	const failures = (fallbackBackoffState.get(key)?.failures ?? 0) + 1
	const delay = Math.min(FALLBACK_BASE_DELAY_MS * 2 ** (failures - 1), FALLBACK_MAX_DELAY_MS)
	fallbackBackoffState.set(key, { failures, nextAttemptAt: Date.now() + delay })
}

function clearFallbackBackoff(key: string) {
	fallbackBackoffState.delete(key)
}

/**
 * Cap how many `fn()` calls run concurrently; excess calls queue and start as
 * earlier ones finish. Used to bound concurrent IDB reads below (see its call
 * site): `decodePubFromBlocks` (@wovin/core) resolves a thread's applog list
 * via `Promise.all(pubLogsArray.map(...))`, and each resolution calls the
 * blockStore's `get()`. For a large thread (tens of thousands of applogs)
 * with no limiter, that fires tens of thousands of *simultaneous* real
 * `IDBBlockstore.get()` calls (each its own transaction) — enough to exhaust
 * the tab's resources and crash it, observed while decoding a partially-synced
 * ~90K-block thread from local IDB. Bounding concurrency keeps throughput high
 * without the thundering herd.
 */
function createConcurrencyLimiter(maxConcurrent: number) {
	let active = 0
	const queue: Array<() => void> = []
	const runNext = () => {
		if (active >= maxConcurrent || queue.length === 0) return
		active++
		queue.shift()!()
	}
	return async function limit<T>(fn: () => Promise<T>): Promise<T> {
		await new Promise<void>(resolve => {
			queue.push(resolve)
			runNext()
		})
		try {
			return await fn()
		} finally {
			active--
			runNext()
		}
	}
}

/**
 * Public entry point — reports live pull progress (keyed by `ipns`) into the
 * transfer-progress store, then delegates to the inner retrieval. Progress is
 * emitted fire-and-forget from the worker so it never gates the transfer.
 */
export const retrievePubToThread = async function(
	thread: WriteableThread,
	ipns: IpnsString,
	opts: { pinCID?: string; lastCID?: string; subID?: string; progressKey?: string } = {},
	appThread: Thread,
) {
	// Progress is keyed by an explicit, stable key the UI can reference. The `ipns`
	// arg is normalized (or null for CID previews), so callers pass `progressKey`
	// (the raw previewID / sub id) to guarantee the preview page + tooltip match.
	const key = opts.progressKey ?? ipns
	reportTransferProgress(key, { kind: 'pull', source: 'car-mirror', phase: 'resolving' })
	try {
		const result = await retrievePubToThreadInner(thread, ipns, opts, appThread)
		if ((result as { didUpdate?: boolean })?.didUpdate === false) clearTransferProgress(key)
		else reportTransferProgress(key, { kind: 'pull', phase: 'done' })
		return result
	} catch (err) {
		reportTransferProgress(key, { kind: 'pull', phase: 'error', error: err instanceof Error ? err.message : String(err) })
		throw err
	}
}

const retrievePubToThreadInner = async function(
	thread: WriteableThread,
	ipns: IpnsString,
	{ lastCID, pinCID, subID, progressKey }: { pinCID?: string; lastCID?: string; subID?: string; progressKey?: string } = {},
	appThread: Thread,
) {
	DEBUG('Retrieving Applogs from', { ipns, lastCID, pinCID, thread })

	// Proxied progress sink shared by both the car-mirror and gateway worker paths.
	// Keyed by the stable progressKey (falls back to ipns) so the preview panel and
	// sync tooltip both find it.
	const key = progressKey ?? ipns
	const onPullProgress = Comlink.proxy((e: PullProgressEvent) => {
		reportTransferProgress(key, {
			kind: 'pull',
			source: e.source,
			// 'complete' here only means the network layer (worker) finished fetching
			// blocks — decodePubFromBlocks + tryIntegrateApplogs still run on the main
			// thread afterwards. Report 'decoding' so the UI doesn't show "done" while
			// the (potentially slow, e.g. large pub) decode/validate/integrate step is
			// still churning. The wrapper below sets the real 'done' once that finishes.
			phase: e.phase === 'complete' ? 'decoding' : 'transferring',
			round: e.round,
			maxRounds: e.maxRounds,
			blocks: e.blocks,
			blocksThisRound: e.blocksThisRound,
			bytes: e.bytes,
			bytesThisRound: e.bytesThisRound,
			frontier: e.frontier,
			bloomBytes: e.bloomBytes,
			bloomK: e.bloomK,
			requests: e.requests,
			snapshots: e.snapshots,
		})
	})

	let cid: CID
	const ts = new Date()
	if (pinCID) {
		cid = parseCID(pinCID)
	} else {
		try {
			cid = pinCID ? parseCID(pinCID) : (await resolveIPNS(ipns))
			if (!cid) throw ERROR(`Could not resolve ipns: ${ipns}`)
		} catch (err) {
			throw ERROR(`Failed to resolve IPNS`, ipns, err)
		}
	}
	if (lastCID && areCidsEqual(cid.toV1().toString(), lastCID)) {
		LOG(`[retrieve] IPNS value === Last CID`, { lastCID, cid })
		return { cid, didUpdate: false }
	}

	// Identifies this retrieval target for the full-chain-fallback backoff below —
	// the ipns name if we're pulling a subscription, else the pinned CID (previews).
	const fallbackBackoffKey = ipns ?? cid.toString()

	// const currentAppThread = withoutDeleted(lastWriteWins(appThread))
	// const gatewayProviderIDs = getProviderIDs(['ipfs-gateway', 'ucan-store-proxy'], onlyFromAgent(currentAppThread, ag))
	// const gateways = gatewayProviderIDs.map(id => {
	// 	const resultArr = filterAndMap(currentAppThread, { en: id, at: 'wovin/provider/url' }, 'vl')
	// 	if (resultArr.length !== 1) throw ERROR(`provider attr get length != 1`, id, resultArr, currentAppThread)
	// 	// const url = getVM(ProviderVM, id).url
	// 	return { url: resultArr[0] } // url.includes('{CID}') ? :`${urlFromHumanInput(url)}/ipfs/{CID}/?format=car&dag-scope=all`
	// })
	const gateways = [...storageState.gateways]
	if (hasKubo()) {
		const kuboGW = getKuboGateway()
		if (!gateways.some(g => g.url === kuboGW.url)) {
			gateways.push(kuboGW as unknown as ProviderVM) // HACK kubo as GW
		}
	}

	let carBlocks: DecodedCar
	// if (isFileSystemAPIavailable()) {
	// 	const dirHandle = await agent.getDirectoryHandle()
	// 	if (dirHandle && await verifyPermission(dirHandle, true)) {
	// 		try {
	// 			const carFile = await getFile(dirHandle, `${ipns}/${cid}`)
	// 			if (carFile) car = await CarReader.fromBytes(new Uint8Array(await carFile.arrayBuffer()))
	// 			DEBUG({ dirHandle, carFile, car })
	// 		} catch (error) {
	// 			WARN('car load from local', error)
	// 		}
	// 	}
	// }

	// TODO: load from kubo? but when?

	const stopAtCID = lastCID ? parseStoredCID(lastCID, 'retrievePubToThread stopAtCID') : undefined
	let decodeResult
	// Try up to 2 attempts: first from IDB (no network), then with network fetch if decode fails.
	// Note: root block in IDB does NOT guarantee the full DAG is present (e.g. interrupted sync),
	// so we must be prepared to fall back to network on decode failure.
	for (let attempt = 0; attempt < 2; attempt++) {
		if (!carBlocks && attempt === 0) {
			const pubRootBlock = await getBlockFromStore(cid)
			if (pubRootBlock) {
				DEBUG('[retrieve] root block found in IDB, attempting local decode')
				const limitIdbGet = createConcurrencyLimiter(64)
				carBlocks = {
					rootCID: cid,
					blockStore: {
						get: async (cid) => limitIdbGet(() => getBlockStore().get(cid)),
					},
				}
			}
		}

		// Try car-mirror pull via SyncWorker (uses cached connectors)
		if (!carBlocks) {
			try {
				const SyncWorker = getSyncWorker()
				// stopAtCID (the sub's last fully-pulled root) doubles as the car-mirror
				// boundary: the pull walk stops there instead of re-walking all history.
				const { success: pulled, blocks: pulledBlockPairs } = await SyncWorker.pullDagInWorker(cid, stopAtCID, onPullProgress)
				if (pulled) {
					const store = getBlockStore()
					// Serve straight from the blocks just pulled (already in memory, no IDB
					// round-trip) — same read-avoidance the gateway-fetch path below already
					// uses via its own `inMemoryBlocks` map. decodePubFromBlocks resolves every
					// applog CID via one big Promise.all; without this, that's tens of
					// thousands of individual IDB reads for data we're already holding.
					// Concurrency-limited fallback (createConcurrencyLimiter's doc comment)
					// still guards anything NOT in this pull's own blocks (e.g. a root that was
					// already local from a prior session and thus never passed through putMany).
					const inMemoryBlocks = new Map(pulledBlockPairs ?? [])
					const limitIdbGet = createConcurrencyLimiter(64)
					carBlocks = {
						rootCID: cid,
						blockStore: {
							get: async (cid) => inMemoryBlocks.get(cid.toString()) ?? limitIdbGet(() => store.get(cid)),
						},
					}

					// Verify the root's referenced blocks are actually in the store.
					// pullDagInWorker may only fetch the root; if applogs/info blocks
					// are missing, fall through to gateway which fetches the full chain.
					//
					// The whole check is wrapped defensively: the worker already reported
					// the pull as successful, so this is a best-effort extra sanity check,
					// not the source of truth. `store.get()` (blockstore-idb) rejects rather
					// than resolving falsy on a miss, and — with a large multi-round pull —
					// a spurious rejection here must NOT discard an otherwise-successful
					// pull and force a needless (and, on a storage-constrained profile,
					// potentially quota-busting) full re-fetch via the slow gateway path.
					// A false "looks complete" is cheap to recover from later (the decode
					// step below still validates the actual data); a false "incomplete" is
					// not.
					try {
						const rootBytes = inMemoryBlocks.get(cid.toString()) ?? await store.get(cid)
						if (rootBytes) {
							const root = dagJson.decode(rootBytes) as any
							const missingRefs: CID[] = []
							for (const ref of [root?.applogs, root?.info].filter(Boolean)) {
								// dagJson.decode() normally resolves `{"/": "<cid>"}` links to real CID
								// instances already, so CID.asCID() (duck-typed, safe across differing
								// multiformats copies) is tried first. The `ref['/']` fallback is a
								// defensive parse of an unexpected raw-link shape, NOT the common case.
								let refCid: CID | null = CID.asCID(ref) ?? null
								if (!refCid && ref?.['/']) {
									try {
										refCid = parseCID(ref['/'])
									} catch (err) {
										WARN('[retrieve] could not parse ref as CID, skipping verification for it', ref, err)
									}
								}
								if (refCid) {
									try {
										if (!inMemoryBlocks.has(refCid.toString()) && !(await store.get(refCid))) missingRefs.push(refCid)
									} catch {
										// store.get() rejects on a miss (or a transient error) — either
										// way, treat as "couldn't confirm", not as a hard verification
										// failure serious enough to discard the pull.
									}
								}
							}
							if (missingRefs.length > 0) {
								DEBUG('[retrieve] car-mirror pulled root but missing', missingRefs.length, 'referenced blocks, falling back to gateway')
								carBlocks = null
							}
						} else {
							carBlocks = null
						}
					} catch (err) {
						DEBUG('[retrieve] car-mirror pull verification threw, trusting the worker-reported success', err)
					}

					if (carBlocks) {
						DEBUG('[retrieve] car-mirror pull via worker succeeded')
					}
				}
			} catch (err) {
				DEBUG('[retrieve] car-mirror pull via worker failed, falling back to gateway', err)
			}
		}

		if (!carBlocks) {
			// Quota-busting full-chain gateway fetch — see fallbackBackoffState doc above.
			// Back off exponentially per-target instead of retrying every sync tick.
			const backedOff = isFallbackBackedOff(fallbackBackoffKey)
			if (backedOff) {
				throw ERROR(
					`[retrieve] full-chain gateway fallback backed off for this target (${backedOff.failures} consecutive fallback(s)), next attempt at ${
						new Date(backedOff.nextAttemptAt).toISOString()
					}`,
					{ ipns, cid: cid.toString() },
				)
			}
			recordFallbackAttempt(fallbackBackoffKey)
			DEBUG('pub from car situation')
			if (!gateways.length) {
				if (window.location.host !== 'localhost') {
					WARN('No retrieval providers, scheduling reload...', storageState)
					setTimeout(() => {
						if (!storageState.gateways.length) { // check if still not fixed
							WARN('Still no retrieval providers, reloading...')
							window.location.reload() // HACK: to un-break UX as long as this bug is not fixed
						}
					}, 1000)
				}
				throw ERROR(`No retrieval providers (reload might fix it - or don't use private window / weird browser - in any case: tell manu)`)
			}
			// if (!gateways.length) throw ERROR(`No retrieval providers configured (Settings > Storage)`)
			const SyncWorker = getSyncWorker()
			const fetchResult = await SyncWorker.fetchSnapshotChainInWorker(
				cid,
				gateways.map(gw => ({ url: gw.url })),
				stopAtCID,
				onPullProgress,
			)
			DEBUG('fetchResult from SyncWorker', { snapshotCount: fetchResult.snapshotCount, blockCount: fetchResult.blocks.length })

			// Reconstruct blockStore from serializable blocks array
			const blocksMap = new Map<string, Uint8Array>(fetchResult.blocks)
			if (blocksMap.size === 0) {
				throw ERROR(`Remote publication has no block data (CAR was empty). The content may not have been published correctly.`, {
					cid: cid.toString(),
				})
			}
			// Persist gateway-fetched blocks to IDB so they're available for future chain walks.
			// Single-transaction batch write - see putManyBlocks doc for why (was one transaction
			// per block, ~90s for an 80k-block pull).
			const idbStore = getBlockStore()
			const pairs = Array.from(blocksMap, ([cidStr, bytes]) => ({ cid: parseCID(cidStr), bytes }))
			// Keyed by the CID's own .toString() (not the raw worker-supplied cidStr, which may use a
			// different encoding) so lookups below match however decodePubFromBlocks re-derives CIDs.
			const inMemoryBlocks = new Map(pairs.map(({ cid: c, bytes }) => [c.toString(), bytes]))
			await putManyBlocks(pairs)
			DEBUG('[retrieve] persisted gateway blocks to IDB', { count: blocksMap.size })
			carBlocks = {
				rootCID: cid,
				blockStore: {
					// Serve from the blocks we just fetched (in memory, already have them) instead of
					// reading back from IDB - IDBBlockstore.get() opens its own transaction per call,
					// so re-fetching ~80k blocks one at a time from IDB right after writing them was
					// the single biggest cost in the whole pull (118s -> ~2s once fixed).
					// Falls back to IDB for anything not in this fetch (e.g. blocks from a prior pull,
					// referenced when the chain walk continues past stopAtCID).
					get: async (c: CID) => inMemoryBlocks.get(c.toString()) ?? idbStore.get(c),
				},
			}
			DEBUG('carBlocks from SyncWorker', carBlocks)
		}

		try {
			decodeResult = await decodePubFromBlocks(carBlocks, [], stopAtCID)
			// Whatever path got us here (car-mirror or gateway fallback) worked out —
			// clear any accumulated backoff so a future genuine failure starts fresh.
			clearFallbackBackoff(fallbackBackoffKey)
			break // success
		} catch (err) {
			if (attempt === 0) {
				WARN('[retrieve] decode failed, will fetch missing blocks from network', err)
				carBlocks = null // reset so next iteration tries car-mirror/gateway
			} else {
				throw err
			}
		}
	}
	const { applogs, cid: decodedCID, applogsCID, info } = decodeResult
	// TODO: addBlocksToStore(carBlocks.blockStore.all().filter(b => !containsCid(applogCids, b.cid)).map(block => ({ cid: block.cid, block: block.bytes })))
	DEBUG('decoded car', { applogs, cid, applogsCID, info })
	if (cid.toString() !== decodedCID.toString()) {
		throw ERROR(
			`Decoded CID does not match original CID... something is wrong as I don't think you're important enough for an adversarial MITM`,
			{ ipns, cid, decodedCID },
		)
	}

	const deduplicatedLogs = sanityCheckLogs(applogs)
	// if (deduplicated) {
	// 	WARN('encryptedFor has changed', { subToUpdate, encryptedFor: encryptedForCurrentAgent })
	// }
	const { hasDecryptionErrors, encryptedLogs, encryptedForCurrentAgent } = await tryIntegrateApplogs(
		deduplicatedLogs,
		info.logs,
		// ag,
		thread,
		subID,
		cid,
	)
	VERBOSE('tryIntegrateApplogs returned:', { deduplicatedLogs, hasDecryptionErrors, encryptedLogs, encryptedForCurrentAgent })
	if (hasDecryptionErrors) {
		throw ERROR(`Failed to decrypt (${encryptedLogs.length}/${deduplicatedLogs.length}) in publication ${ipns}`)
		// TODO: continue with the parts we got?
	}

	// Extract prevCounter from snapshot root block
	let counterInfo: { counter?: number } | undefined
	try {
		const rootBytes = await carBlocks.blockStore.get(cid)
		if (rootBytes) {
			const root = dagJson.decode(rootBytes) as SnapRootBlock
			if (typeof root.prevCounter === 'number') {
				counterInfo = { counter: root.prevCounter }
			}
		}
	} catch (err) {
		DEBUG('Could not extract counter from snapshot root', err)
	}

	return {
		didUpdate: true,
		ts,
		cid,
		encryptedFor: encryptedForCurrentAgent,
		applogsCID,
		info,
		applogs: deduplicatedLogs,
		counterInfo,
	}
}

export async function resolveIPNS(ipns: IpnsString) {
	// TODO: Refactor kubo integration into a @wovin/connect-kubo package for consistency with other connectors
	// Start kubo resolver in background if available
	const kuboPromise = hasKubo() ?
		(async () => {
			try {
				const results = await asyncIterableToArray(kuboClient.name.resolve(ipns))
				DEBUG(`[resolveIPNS] kubo results:`, results)
				if (results.length == 0) return null
				if (results.length > 1) WARN(`[resolveIPNS] multiple results from kubo - precedence not implemented:`, results)
				const result = results[0]

				if (!result.startsWith('/ipfs/')) {
					WARN("IPNS value is not '/ipfs/..':", result)
				}
				return parseCID(result.replace('/ipfs/', ''))
			} catch (err) {
				DEBUG(`[resolveIPNS] kubo error:`, err)
				return null
			}
		})() :
		Promise.resolve(null)

	const connectors = storageState.ucanUploads ?? []
	const gateways = storageState.ipnsGatewayResolvers ?? []
	const w3nameResolvers = storageState.ipnsW3NameResolvers ?? []
	if (connectors.length === 0 && !hasKubo() && gateways.length === 0 && w3nameResolvers.length === 0) {
		throw ERROR(
			`No IPNS resolution targets configured for ${ipns} (no ucan-store-proxy providers, no kubo, no gateways, no w3name resolvers)`,
		)
	}

	// Fan out to every ucan-store-proxy connector AND kubo. First success wins.
	// kubo is always raced even when connectors say "never published" — the original
	// publishing server (e.g. w3name) may be dead, but the record could still resolve
	// via the IPNS DHT. IPNSNeverPublishedError is only decisive when EVERY target
	// (including kubo) fails.
	const connectorResults = await Promise.allSettled(
		connectors.map(async (connector: UcanStoreProxyConnector) => {
			const cid = await connector.resolveIPNS(ipns)
			DEBUG(`[resolveIPNS] using ucan-store-proxy result from ${connector.baseUrl}`)
			return cid
		}),
	)

	for (const r of connectorResults) {
		if (r.status === 'fulfilled') return r.value
		DEBUG(`[resolveIPNS] ucan-store-proxy attempt failed:`, r.reason)
	}

	// All ucan-store-proxy attempts failed (or there were none) — fall back to kubo
	const kuboResult = await kuboPromise
	if (kuboResult) {
		DEBUG(`[resolveIPNS] ucan-store-proxy unavailable, using kubo DHT result`)
		return kuboResult
	}

	// kubo also failed — try public IPFS gateways as a last resort. These resolve
	// IPNS → CID via the gateway's `X-Ipfs-Roots` header. Configurable via
	// VITE_PUBLIC_IPFS_GATEWAYS (default: https://ipfs.zt.ax).
	if (gateways.length > 0) {
		const gatewayCid = await resolveIPNSViaGateway(ipns, gateways)
		if (gatewayCid) {
			DEBUG(`[resolveIPNS] resolved via public IPFS gateway`)
			return gatewayCid
		}
	}

	// Public gateways also failed — fall back to the w3name naming service. Graceful
	// transition: names published to w3name (name.web3.storage) but not yet propagated to
	// the public IPFS DHT still resolve here, matching the previous (live) behaviour.
	if (w3nameResolvers.length > 0) {
		const w3nameCid = await resolveIPNSViaW3Name(ipns, w3nameResolvers)
		if (w3nameCid) {
			DEBUG(`[resolveIPNS] resolved via w3name naming service`)
			return w3nameCid
		}
	}

	// Everything failed — surface the most informative error:
	// IPNSResolutionError (server reachable but other issue) is more actionable than
	// IPNSNeverPublishedError (which often just means the original publisher is down).
	const lastResolutionErr = connectorResults
		.map(r => (r.status === 'rejected' ? r.reason : null))
		.find(reason => reason instanceof IPNSResolutionError)
	if (lastResolutionErr) throw lastResolutionErr

	const lastNeverPublishedErr = connectorResults
		.map(r => (r.status === 'rejected' ? r.reason : null))
		.find(reason => reason instanceof IPNSNeverPublishedError)
	if (lastNeverPublishedErr) throw lastNeverPublishedErr

	throw ERROR(
		`All IPNS resolution attempts failed for ${ipns} `
			+ `(tried: ${connectors.length} ucan-store-proxy connector(s), `
			+ `${hasKubo() ? 'kubo, ' : ''}${gateways.length} public IPFS gateway(s), `
			+ `${w3nameResolvers.length} w3name resolver(s))`,
	)
}
